import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  audienceSentence,
  shortChannelLabel,
  smsCostNote,
  unreachableNote,
  type ChannelAudience,
} from '../../../lib/broadcastChannels';
import { audienceLabel } from './audience';

/**
 * The "Send broadcast?" confirmation, extracted out of `BroadcastsTab.tsx` to
 * stay under its 400-line contract cap (see `broadcasts.contract.test.ts`) —
 * this component carries no state of its own, so extracting it changes
 * nothing about behaviour, only where the JSX lives.
 */
export default function SendConfirmDialog({
  open,
  onClose,
  onConfirm,
  plans,
  segment,
  smsSelected,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  plans: readonly ChannelAudience[];
  segment: string;
  smsSelected: boolean;
}) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Send broadcast?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You're about to send {audienceSentence(plans)}. Audience: {audienceLabel(segment)}.
        </DialogContentText>
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          {plans.map((plan) => {
            const unreachable = unreachableNote(plan);
            const cost = smsCostNote(plan);
            if (!unreachable && !cost) return null;
            return (
              <Typography key={plan.channel} variant="body2" color="text.secondary">
                {shortChannelLabel(plan.channel)}: {[unreachable, cost].filter(Boolean).join(' ')}
              </Typography>
            );
          })}
        </Stack>
        {smsSelected ? (
          // Texts cost per segment and land on the concierge line; that belongs
          // in front of the host before the click, not in the preview panel
          // they may have scrolled past.
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Texts send from the concierge line and are billed per segment. There is no opt-out
            in the message — only send to guests who agreed to be texted.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm}>Send now</Button>
      </DialogActions>
    </Dialog>
  );
}
